import { useEffect, useState } from 'react'
import {
  Room,
  RoomEvent,
  Track,
  RemoteTrackPublication,
  RemoteParticipant,
  TrackPublication,
  Participant,
} from 'livekit-client'

interface VideoGridProps {
  room: Room
}

interface TrackEntry {
  trackSid: string
  identity: string
  publication: RemoteTrackPublication
}

/**
 * VideoGrid renders remote camera tracks using raw livekit-client APIs.
 * Avoids @livekit/components-react entirely to prevent hooks from
 * interfering with the Room connection lifecycle.
 */
export default function VideoGrid({ room }: VideoGridProps) {
  const [tracks, setTracks] = useState<TrackEntry[]>([])

  useEffect(() => {
    function rebuildTracks() {
      const entries: TrackEntry[] = []
      for (const participant of room.remoteParticipants.values()) {
        for (const pub of participant.trackPublications.values()) {
          console.log('[VideoGrid] track: identity=%s source=%s subscribed=%s muted=%s hasTrack=%s',
            participant.identity, pub.source, pub.isSubscribed, pub.isMuted, !!pub.track)
          if (
            pub.source === Track.Source.Camera &&
            pub.isSubscribed &&
            pub.track
          ) {
            entries.push({
              trackSid: pub.trackSid,
              identity: participant.identity ?? 'Unknown',
              publication: pub as RemoteTrackPublication,
            })
          }
        }
      }
      console.log('[VideoGrid] rebuildTracks: %d entries', entries.length)
      setTracks(entries)
    }

    // Initial build
    rebuildTracks()

    // Listen for track subscribe/unsubscribe events
    const onTrackSubscribed = (
      _track: Track,
      pub: TrackPublication,
      participant: RemoteParticipant,
    ) => {
      console.log('[VideoGrid] TrackSubscribed: identity=%s source=%s sid=%s', participant.identity, pub.source, pub.trackSid)
      rebuildTracks()
    }

    const onTrackUnsubscribed = (
      _track: Track,
      pub: TrackPublication,
      participant: RemoteParticipant,
    ) => {
      console.log('[VideoGrid] TrackUnsubscribed: identity=%s source=%s sid=%s', participant.identity, pub.source, pub.trackSid)
      rebuildTracks()
    }

    const onParticipantConnected = (participant: RemoteParticipant) => {
      console.log('[VideoGrid] ParticipantConnected: identity=%s', participant.identity)
      rebuildTracks()
    }

    const onParticipantDisconnected = (participant: RemoteParticipant) => {
      console.log('[VideoGrid] ParticipantDisconnected: identity=%s', participant.identity)
      rebuildTracks()
    }

    const onTrackMuted = (pub: TrackPublication, participant: Participant) => {
      console.log('[VideoGrid] TrackMuted: identity=%s source=%s sid=%s', participant.identity, pub.source, pub.trackSid)
      rebuildTracks()
    }

    const onTrackUnmuted = (pub: TrackPublication, participant: Participant) => {
      console.log('[VideoGrid] TrackUnmuted: identity=%s source=%s sid=%s', participant.identity, pub.source, pub.trackSid)
      rebuildTracks()
    }

    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed)
    room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed)
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected)
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected)
    room.on(RoomEvent.TrackMuted, onTrackMuted)
    room.on(RoomEvent.TrackUnmuted, onTrackUnmuted)

    return () => {
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed)
      room.off(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed)
      room.off(RoomEvent.ParticipantConnected, onParticipantConnected)
      room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected)
      room.off(RoomEvent.TrackMuted, onTrackMuted)
      room.off(RoomEvent.TrackUnmuted, onTrackUnmuted)
    }
  }, [room])

  return (
    <div className="bg-white shadow rounded-lg p-4">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Live Streams</h2>
      {tracks.length === 0 ? (
        <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
          No participants are streaming yet
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {tracks.map((entry) => (
            <VideoTrackView
              key={entry.trackSid}
              entry={entry}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function VideoTrackView({ entry }: { entry: TrackEntry }) {
  useEffect(() => {
    // Attach the video track to a <video> element
    const el = document.getElementById(`video-${entry.trackSid}`) as HTMLVideoElement | null
    if (el && entry.publication.track) {
      entry.publication.track.attach(el)
    }
    return () => {
      if (entry.publication.track) {
        entry.publication.track.detach()
      }
    }
  }, [entry.trackSid, entry.publication.track])

  return (
    <div className="relative bg-gray-900 rounded-lg overflow-hidden aspect-video">
      <video
        id={`video-${entry.trackSid}`}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover"
      />
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
        <span className="text-white text-xs font-medium truncate block">
          {entry.identity}
        </span>
      </div>
    </div>
  )
}
